<template>
  <div>
    <v-card block class="px-3 px-sm-6 py-0 d-flex d-print-none flex-row justify-space-between align-center renewal-toolbar" color="primary" dark
            :key="$_sideMenu"  large sticky-offset="$_offset" sticky-z-index="99"
            style="border-top: 1px solid white !important" tile v-sticky>
      <div class="d-flex align-center flex-nowrap renewal-toolbar__primary">
        <information-dialog-icon confirmClass="mr-3 mb-1 title" confirmColor="secondary darken-1"
                                 icon-color="white" title="Renewal Report"
                                 tclass="title grey lighten-2 mb-4">
          <div class="renewal-info-dialog">
            <p class="mb-3"><strong>Status</strong> and <strong>Producer</strong> are required. Status defaults to <strong>Book</strong>, and Producer defaults to your username when that user appears in the producer list.</p>
            <p class="mb-3">If your username is not listed, choose a producer, then press <strong>Enter</strong> in Status or Producer, or click <strong>RENEWAL REPORT</strong>.</p>
            <p class="mb-3"><strong>Rows per Page</strong> controls server-side pagination. Column sorting in the table applies only to the rows on the <strong>current page</strong>.</p>
            <p class="mb-3"><strong>Expiry months (server)</strong> limits what the API returns. Changing months does not refresh the report by itself—press <strong>Enter</strong> in that field after selections, or click <strong>RENEWAL REPORT</strong> (same as the filter hint).</p>
            <p class="mb-3 text--secondary">When status is <strong>Book</strong>, <strong>Report totals</strong> below the table come from the API: counts and premiums by placement type (New Biz, Recovered, Renewal, Other) plus Total, across all matching report pages—not only the page you see. For <strong>Current</strong> and <strong>Lost</strong>, that API totals layout is not shown.</p>
            <p v-if="$_sideMenu" class="mb-3">For better print results, switch to the <strong>top menu</strong> using the lion in the upper-left corner.</p>
            <p class="mb-0">Printed output depends on your browser and printer. Click <v-icon>mdi-printer</v-icon> for <strong>print guidance</strong>, then print.</p>
          </div>
        </information-dialog-icon>
        <button-tip btnclass="px-2 px-sm-6" @click="getRenewal()" :disabled="loading || !canRunRenewal"
                    hideSmDownAfter=" Report" large text tile tip="Get renewal data" txt="Renewal "/>
      </div>
      <div class="d-flex align-center flex-nowrap renewal-toolbar__secondary">
        <v-tooltip bottom open-delay="200" close-delay="150">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-btn class="px-2 px-sm-8" color="white" @click="printDiv()"
                     :disabled="loading || !searchResultLoadedShow || !renewalData.length"
                     text tile>
                <v-icon color="white" :class="{ 'mr-1': !$vuetify.breakpoint.smAndDown }">mdi-printer</v-icon>
                <span class="hidden-sm-and-down">Print</span>
              </v-btn>
            </span>
          </template>
          <span>Open print dialog</span>
        </v-tooltip>
      </div>
      <div class="renewal-toolbar__reset">
        <button-tip btnclass="px-2 px-sm-8" @click="clearSearch()" :disabled="loading"
                    hideSmDownAfter=" Defaults" large text tile tip="Reset Status and Producer to default values"
                    txt="Reset"/>
      </div>
    </v-card>
    <v-card tile class="mx-0 px-1 pt-0 pb-0 mt-0 mt-sm-3 mb-2 d-print-none elevation-18 renewal-filter-card">
      <v-row dense align="start" class="mx-0 pl-0">
        <v-col :class="['pt-6 pt-sm-8 renewal-filter-col renewal-filter-col--months', expiryMonthFieldLayoutClass]" cols="12" sm="6" md="3">
          <div class="renewal-filter-group">
            <search-input ref="expiryMonthsInput"
                          class="renewal-filter-input renewal-expiry-months-input renewal-expiry-months-field"
                          chips
                          deletable-chips
                          dense
                          @enter="getRenewalAfterSelectionSettles()"
                          hint="Press Enter or click Renewal Report to apply filters"
                          item-text="text"
                          item-value="value"
                          keep-menu-open-on-select
                          label="Expiry months (server)"
                          :items="expiryMonthOptions"
                          :menu-props="{ closeOnContentClick: false, maxHeight: 320 }"
                          multiple
                          persistent-hint
                          :selection-text="getExpiryMonthSelectionText"
                          small-chips
                          :type="'VSelect'"
                          v-model="selectedExpiryMonths"/>
          </div>
        </v-col>
        <v-col class="pt-6 pt-sm-8 renewal-filter-col" cols="12" sm="6" md="3">
          <div class="renewal-filter-group">
            <v-autocomplete
                ref="statusInput"
                v-model="status"
                :search-input.sync="statusSearch"
                class="renewal-filter-input"
                dense
                :error="showStatusRequiredHint || showStatusInvalidHint"
                item-text="text"
                item-value="value"
                :label="statusInputLabel"
                :items="statusOptions"
                @keydown.enter.prevent="getRenewal"
            />
          </div>
        </v-col>
        <v-col class="pt-6 pt-sm-8 renewal-filter-col" cols="12" sm="6" md="3">
          <div class="renewal-filter-group">
            <v-autocomplete
                ref="producerInput"
                v-model="producer"
                :search-input.sync="producerSearch"
                class="renewal-filter-input"
                dense
                :error="showProducerRequiredHint || showProducerInvalidHint"
                :label="producerInputLabel"
                :items="producerSelectItems"
                @keydown.enter.prevent="getRenewal"
            />
          </div>
        </v-col>
        <v-col class="pt-6 pt-sm-8 renewal-filter-col" cols="12" sm="6" md="3">
          <div class="renewal-filter-group">
            <v-select
                v-model="itemsPerPage"
                class="renewal-filter-input"
                dense
                item-text="text"
                item-value="value"
                label="Rows per Page (server)"
                :items="rowsPerPageOptions"
                @change="onRenewalItemsPerPageChange"
            />
          </div>
        </v-col>
      </v-row>
    </v-card>
    <div ref="renewalResultsSection" class="px-4 pb-0 renewal-results-section" v-if="loading || searchResultLoadedShow">
      <div class="mx-6 mt-6 mb-3 renewalheader text-center" style="line-height: 1.2" v-if="renewalData.length > 0">
        <h4 class="mb-0">Renewal Report</h4>
        <h5 class="mb-0">{{ reportFilterSummary }}</h5>
        <h6 v-if="printMonthFilterSummary" class="mb-0 renewal-print-view-filter-note">{{ printMonthFilterSummary }}</h6>
        <h6 class="mb-0 renewal-print-page-note">{{ printPageRowsSummary }}</h6>
        <h5 class="mb-0 renewal-total-rows">{{ visibleRowSummary }}</h5>
      </div>
      <div v-if="loading" class="renewal-loading-state">
        <v-progress-circular
            color="primary"
            indeterminate
            :size="64"
            :width="6"
        />
        <div class="renewal-loading-text mt-5">Loading renewal report data...</div>
      </div>
      <div v-if="showEmptyResultsState" class="renewal-empty-results-spacer"></div>
      <v-data-table v-if="!loading"
                    ref="renewalTable"
                    :class="[
                      'px-0 pb-0 mx-0 mt-0 mb-0 elevation-4 left med-table-rows prtrenewal striped-table table-styl',
                      { 'renewal-results-table--empty': showEmptyResultsState }
                    ]"
                    disable-pagination
                    fixed-header
                    :height="renewalTableHeight"
                    :headers="renewalHeaders"
                    :items="sortedFilteredRenewalData"
                    hide-default-footer
                    :loading="loading"
                    loading-text="Loading... Please wait"
                    :mobile-breakpoint="0"
                    multi-sort
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                    :no-data-text="renewalNoDataText">
        <template v-slot:no-data>
          <div class="renewal-empty-state">
            {{ renewalNoDataText }}
          </div>
        </template>
        <template v-slot:item.expiryMonthSortValue="{ item }">
          <span>{{ item.expiryMonthDisplay }}</span>
        </template>
        <template v-slot:item.expiryDaySortValue="{ item }">
          <span>{{ item.expiryDayDisplay }}</span>
        </template>
        <template v-slot:item.expiryYearSortValue="{ item }">
          <span>{{ item.expiryYearDisplay }}</span>
        </template>
        <template v-slot:item.premiumValue="{ item }">
          <span class="renewal-premium-cell">{{ item.premium }}</span>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center align-center renewal-actions" style="width: 100%; gap: 2px;">
            <button-tip
                v-if="item.pid && canPolicyEdit"
                @click="$_editPolicy(item.pid)"
                icon
                iname="mdi-pencil"
                small
                tip="Edit Policy"
            />
            <button-tip
                v-if="item.fid && canFirmView"
                @click="$_viewFirmNoNotePopup(item.fid)"
                icon
                iname="mdi-open-in-new"
                small
                tip="Open Firm"
            />
          </div>
        </template>
      </v-data-table>
      <div v-if="showRenewalTotalsBand" ref="renewalTotalsBand" class="renewal-totals-band">
        <div class="renewal-totals-band__heading">
          <span class="renewal-totals-band__title">{{ renewalTotalsTitle }}</span>
          <span v-if="renewalServerTotalsInlineScopeCaption" class="renewal-totals-band__inline-caption caption text--secondary">
            - {{ renewalServerTotalsInlineScopeCaption }}
          </span>
        </div>
        <div class="renewal-totals-grid renewal-totals-grid--headers" :style="renewalTotalsGridStyle">
          <div v-for="section in renewalTotalsSections" :key="`${section.key}-header`" class="renewal-totals-section">
            <div class="renewal-totals-section__title">{{ section.label }}</div>
          </div>
        </div>
        <div class="renewal-totals-grid renewal-totals-grid--labels" :style="renewalTotalsGridStyle">
          <div v-for="section in renewalTotalsSections" :key="`${section.key}-labels`" class="renewal-totals-section renewal-totals-section--labels">
            <span class="renewal-totals-metric-label">Count</span>
            <span class="renewal-totals-metric-label">Premium</span>
          </div>
        </div>
        <div class="renewal-totals-grid renewal-totals-grid--values" :style="renewalTotalsGridStyle">
          <div v-for="section in renewalTotalsSections" :key="`${section.key}-values`" class="renewal-totals-section renewal-totals-section--values">
            <span class="renewal-totals-metric-value">{{ formatCount(section.count) }}</span>
            <span class="renewal-totals-metric-value">{{ formatCurrency(section.premium) }}</span>
          </div>
        </div>
      </div>
      <div v-if="!loading && searchResultLoadedShow" ref="renewalTableFooter" class="renewal-table-footer">
        <div class="renewal-table-footer__rows">Rows per page: {{ formatCount(itemsPerPage) }}</div>
        <div class="renewal-table-footer__spacer"></div>
        <div class="renewal-table-footer__range">{{ renewalPageRangeSummary }}</div>
        <v-btn icon small :disabled="loading || !hasPreviousRenewalPage" @click="onRenewalPageChange(currentPage - 1)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon small :disabled="loading || !hasNextRenewalPage" @click="onRenewalPageChange(currentPage + 1)">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
      <div v-if="showRenewalTotalsBand" class="renewal-totals-band renewal-totals-band--print">
        <div class="renewal-totals-band__heading">
          <span class="renewal-totals-band__title">{{ renewalTotalsTitle }}</span>
          <span v-if="renewalServerTotalsInlineScopeCaption" class="renewal-totals-band__inline-caption caption text--secondary">
            - {{ renewalServerTotalsInlineScopeCaption }}
          </span>
        </div>
        <div class="renewal-totals-grid renewal-totals-grid--headers" :style="renewalTotalsGridStyle">
          <div v-for="section in renewalTotalsSections" :key="`print-${section.key}-header`" class="renewal-totals-section">
            <div class="renewal-totals-section__title">{{ section.label }}</div>
          </div>
        </div>
        <div class="renewal-totals-grid renewal-totals-grid--labels" :style="renewalTotalsGridStyle">
          <div v-for="section in renewalTotalsSections" :key="`print-${section.key}-labels`" class="renewal-totals-section renewal-totals-section--labels">
            <span class="renewal-totals-metric-label">Count</span>
            <span class="renewal-totals-metric-label">Premium</span>
          </div>
        </div>
        <div class="renewal-totals-grid renewal-totals-grid--values" :style="renewalTotalsGridStyle">
          <div v-for="section in renewalTotalsSections" :key="`print-${section.key}-values`" class="renewal-totals-section renewal-totals-section--values">
            <span class="renewal-totals-metric-value">{{ formatCount(section.count) }}</span>
            <span class="renewal-totals-metric-value">{{ formatCurrency(section.premium) }}</span>
          </div>
        </div>
      </div>
    </div>

    <ErrorDialog ref="errorDialog"/>
    <action-dialog v-model="showPrintDialog" max-width="360" title="Before You Print" confirm-text="Continue to Print"
                   close-text="Cancel" @confirm="confirmPrint" @close="showPrintDialog = false">
      <p class="mb-2">You are printing the rows currently shown on this report page.</p>
      <p class="mb-2">Report totals include every policy matching the selected filters, across all report pages.</p>
      <p class="mb-2">{{ printPaginationClarifier }}</p>
      <p class="print-settings-alert mb-0"><strong>Use Landscape, Default margins, and Print backgrounds.</strong></p>
    </action-dialog>

  </div>
</template>

<script>
  import currency from 'currency.js';
  import ActionDialog from "../ActionDialog";
  import ErrorDialog from "../ErrorDialog";
  import InformationDialogIcon from "../InformationDialogIcon";
  import { ReportsAPIService } from '@/servicehandlers/ReportsAPIService';
  import { UsersAPIService } from '@/servicehandlers/UsersAPIService';

  const reportsAPIService = new ReportsAPIService();
  const usersAPIService = new UsersAPIService();
  const DEFAULT_RENEWAL_PER_PAGE = 1000; // 7 May 2026 JCH: Default server page size for renewal results.
  const ALL_MONTHS_VALUE = '__all_months__';
  const EXPIRY_MONTH_OPTIONS = [
    { text: 'January', shortText: 'Jan', value: 1 },
    { text: 'February', shortText: 'Feb', value: 2 },
    { text: 'March', shortText: 'Mar', value: 3 },
    { text: 'April', shortText: 'Apr', value: 4 },
    { text: 'May', shortText: 'May', value: 5 },
    { text: 'June', shortText: 'Jun', value: 6 },
    { text: 'July', shortText: 'Jul', value: 7 },
    { text: 'August', shortText: 'Aug', value: 8 },
    { text: 'September', shortText: 'Sep', value: 9 },
    { text: 'October', shortText: 'Oct', value: 10 },
    { text: 'November', shortText: 'Nov', value: 11 },
    { text: 'December', shortText: 'Dec', value: 12 }
  ];
  const EXPIRY_MONTH_VALUES = EXPIRY_MONTH_OPTIONS.map(({ value }) => value);
  const RENEWAL_FIELDS = [
    'pid',
    'fid',
    'insured',
    'carrier',
    'type',
    'status',
    'is_new',
    'expiry',
    'premium',
    'prodname',
    'subid'
  ];

  export default {
    name: 'Renewal',
    components: { ActionDialog, ErrorDialog, InformationDialogIcon },

    data() {
      return {
        activeRenewalRequestKey: '',
        currentPage: 1,
        currentUsername: '',
        itemsPerPage: DEFAULT_RENEWAL_PER_PAGE,
        itemsPerPageOptions: [100, 250, 500, 1000, 3000],
        loading: false,
        producer: '',
        producerOptions: [],
        producerSearch: null,
        expiryMonthChipRowCount: 1,
        renewalTableBottomReservedHeight: 0,
        renewalTableViewportTop: 0,
        selectedExpiryMonths: [],
        renewalData: [],
        /** 7 May 2026 JCH: API `footers` (Book buckets + Total); only meaningful when status is Book. */
        renewalServerFooters: null,
        renewalTotalCount: 0,
        searchResultLoadedShow: false,
        showPrintDialog: false,
        showProducerInvalidHint: false,
        showProducerRequiredHint: false,
        statusSearch: null,
        showStatusInvalidHint: false,
        showStatusRequiredHint: false,
        sortBy: ['expiryYearSortValue', 'expiryMonthSortValue', 'expiryDaySortValue'],
        sortDesc: [false, false, false],
        status: 'book',
        statusOptions: [
          { text: 'Book', value: 'book' },
          { text: 'Current', value: 'current' },
          { text: 'Lost', value: 'lost' }
        ],
        viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 900
      };
    },

    computed: {
      canPolicyEdit() {
        return this.$_can('policy-edit');
      },

      canFirmView() {
        return this.$_can('firm-view');
      },

      canRunRenewal() {
        return !!this.status && !!this.producer;
      },

      showActionsColumn() {
        return this.canPolicyEdit || this.canFirmView;
      },

      producerSelectItems() {
        return this.producerOptions;
      },

      statusInputLabel() {
        if (this.showStatusInvalidHint) return 'Status (pick Book, Current, or Lost)';
        if (this.showStatusRequiredHint) return 'Status (required)';
        return 'Status (required, defaults to Book)';
      },

      rowsPerPageOptions() {
        return this.itemsPerPageOptions.map((value) => ({
          text: this.formatCount(value),
          value
        }));
      },

      producerInputLabel() {
        if (this.showProducerInvalidHint) return 'Producer (pick a listed producer)';
        if (this.showProducerRequiredHint) return 'Producer (required)';
        if (this.producer && this.producer === this.currentUsername) return 'Producer (required, defaults to you)';
        if (this.getDefaultProducer(this.producerOptions)) return 'Producer (required, defaults to you)';
        return 'Producer (required)';
      },

      expiryMonthOptions() {
        return [
          { text: 'All Months', shortText: 'All', value: ALL_MONTHS_VALUE },
          ...EXPIRY_MONTH_OPTIONS
        ];
      },

      hasAllExpiryMonthsSelected() {
        return this.getNormalizedExpiryMonthValues(this.selectedExpiryMonths).length === EXPIRY_MONTH_VALUES.length;
      },

      isExpiryMonthFilterActive() {
        return this.getNormalizedExpiryMonthValues(this.selectedExpiryMonths).length > 0 && !this.hasAllExpiryMonthsSelected;
      },

      /** 7 May 2026 JCH: Server applies status, producers, and expiryMonth. */
      filteredRenewalData() {
        return this.renewalData;
      },

      isBookRenewalQuery() {
        return String(this.status === null || this.status === undefined ? '' : this.status).trim().toLowerCase() === 'book';
      },

      sortedFilteredRenewalData() {
        const rows = [...this.filteredRenewalData];
        const sortKeys = Array.isArray(this.sortBy) ? this.sortBy.filter(Boolean) : (this.sortBy ? [this.sortBy] : []);
        const sortDirections = Array.isArray(this.sortDesc) ? this.sortDesc : [this.sortDesc];

        if (!sortKeys.length) return rows;

        return rows
          .map((row, index) => ({ row, index }))
          .sort((left, right) => {
            for (let index = 0; index < sortKeys.length; index++) {
              const key = sortKeys[index];
              const comparison = this.compareRenewalSortValues(left.row?.[key], right.row?.[key]);
              if (comparison !== 0) return sortDirections[index] ? -comparison : comparison;
            }

            return left.index - right.index;
          })
          .map(({ row }) => row);
      },

      showRenewalTotalsBand() {
        if (!this.isBookRenewalQuery || this.loading) return false;
        if (this.renewalServerFooters && this.renewalServerFooters.Total) {
          return Number(this.renewalServerFooters.Total.count) > 0;
        }
        return this.filteredRenewalData.length > 0;
      },

      renewalTotalsTitle() {
        if (this.isBookRenewalQuery && this.renewalServerFooters && this.renewalServerFooters.Total) {
          return 'Report Totals';
        }
        return this.isExpiryMonthFilterActive ? 'Filtered page totals' : 'Page totals';
      },

      renewalTotalsScopeCounts() {
        return {
          totalRows: Math.max(Number(this.renewalTotalCount) || 0, this.renewalData.length),
          currentPageRows: this.isExpiryMonthFilterActive ? this.filteredRenewalData.length : this.renewalData.length
        };
      },

      renewalServerTotalsInlineScopeCaption() {
        if (!this.isBookRenewalQuery || !this.renewalServerFooters || !this.renewalServerFooters.Total) {
          return '';
        }
        const { totalRows, currentPageRows } = this.renewalTotalsScopeCounts;
        return `Includes all matching policies (${this.formatCount(totalRows)} total rows) across all report pages. Current server page: ${this.formatCount(currentPageRows)} rows shown.`;
      },

      /**
       * 7 May 2026 JCH: API footers are aggregated over the full filtered result set (no page LIMIT).
       * Label the scope explicitly so totals are not mistaken for “this page only”.
       */
      renewalServerTotalsScopeCaption() {
        if (!this.isBookRenewalQuery || !this.renewalServerFooters || !this.renewalServerFooters.Total) {
          return '';
        }
        const { totalRows, currentPageRows } = this.renewalTotalsScopeCounts;
        return `Total counts and premiums include all matching policies (${this.formatCount(totalRows)} total rows) across all report pages. The table above shows the current server page (${this.formatCount(currentPageRows)} rows shown), based on Rows per Page.`;
      },

      renewalTotalsSections() {
        if (this.isBookRenewalQuery && this.renewalServerFooters && this.renewalServerFooters.Total) {
          return this.renewalTotalsSectionsFromApiFooters(this.renewalServerFooters);
        }

        const totals = {
          newBiz: { key: 'new-biz', label: 'New Biz', count: 0, premium: 0 },
          recovered: { key: 'recovered', label: 'Recovered', count: 0, premium: 0 },
          renewal: { key: 'renewal', label: 'Renewal', count: 0, premium: 0 },
          other: { key: 'other', label: 'Other', count: 0, premium: 0 },
          total: { key: 'total', label: 'Total', count: 0, premium: 0 }
        };

        this.filteredRenewalData.forEach((row) => {
          const bucket = this.getRenewalTotalsBucket(row?.is_new);
          const premiumValue = Number.isFinite(row?.premiumValue) ? row.premiumValue : 0;

          totals[bucket].count += 1;
          totals[bucket].premium += premiumValue;
          totals.total.count += 1;
          totals.total.premium += premiumValue;
        });

        return [totals.newBiz, totals.recovered, totals.renewal, totals.other, totals.total];
      },

      renewalTotalsGridStyle() {
        const breakpoint = this.$vuetify && this.$vuetify.breakpoint;
        const sectionCount = Math.max(this.renewalTotalsSections.length, 1);
        const columnCount = breakpoint && breakpoint.smAndDown
          ? Math.min(sectionCount, 2)
          : sectionCount;

        return {
          gridTemplateColumns: `repeat(${Math.max(columnCount, 1)}, minmax(0, 1fr))`
        };
      },

      showEmptyResultsState() {
        return this.searchResultLoadedShow && !this.loading && !this.filteredRenewalData.length;
      },

      visibleRowSummary() {
        const breakpoint = this.$vuetify && this.$vuetify.breakpoint;
        const rowLabel = breakpoint && breakpoint.smAndDown ? 'Rows' : 'Rows Shown';
        const loadedSummary = this.formatLoadedRenewalCountSummary(this.renewalData.length, this.renewalTotalCount);
        return `${rowLabel}: ${loadedSummary} (${this.renewalPageSummary})`;
      },

      renewalNoDataText() {
        if (this.renewalData.length > 0 && this.isExpiryMonthFilterActive) {
          return 'There is no data available for the selected expiry months.';
        }
        return 'There is no data available for that search.';
      },

      selectedExpiryMonthLabels() {
        if (!this.isExpiryMonthFilterActive) return [];
        const monthLabels = new Map(EXPIRY_MONTH_OPTIONS.map((item) => [item.value, item.text]));
        return this.getNormalizedExpiryMonthValues(this.selectedExpiryMonths)
          .filter((value) => monthLabels.has(value))
          .map((value) => monthLabels.get(value));
      },

      selectedExpiryMonthsSummary() {
        if (!this.isExpiryMonthFilterActive) return 'All expiry months';
        const labels = this.selectedExpiryMonthLabels;
        return labels.length <= 4 ? labels.join(', ') : `${labels.slice(0, 3).join(', ')} +${labels.length - 3} more`;
      },

      expiryMonthFieldLayoutClass() {
        if (this.expiryMonthChipRowCount >= 4) return 'renewal-filter-col--months--four-rows';
        if (this.expiryMonthChipRowCount === 3) return 'renewal-filter-col--months--three-rows';
        if (this.expiryMonthChipRowCount === 2) return 'renewal-filter-col--months--two-rows';
        return '';
      },

      renewalHeaders() {
        const headers = [
          { value: 'insured', text: 'Insured', align: 'left', sortable: true, class: 'insured-col', cellClass: 'insured-col' },
          { value: 'carrier', text: 'Carrier', align: 'left', sortable: true, width: '150px' },
          { value: 'type', text: 'Type', align: 'left', sortable: true, width: '100px' },
          { value: 'status', text: 'Status', align: 'left', sortable: true, width: '110px' },
          { value: 'is_new', text: 'Placement', align: 'left', sortable: true, width: '110px' },
          { value: 'expiryYearSortValue', text: 'xYr', align: 'center', sortable: true, width: '88px' },
          { value: 'expiryMonthSortValue', text: 'xMo', align: 'center', sortable: true, width: '84px' },
          { value: 'expiryDaySortValue', text: 'xDy', align: 'center', sortable: true, width: '84px' },
          { value: 'premiumValue', text: 'Premium', align: 'right', sortable: true, width: '120px', class: 'premium-col', cellClass: 'premium-col' },
          { value: 'producer', text: 'Producer', align: 'left', sortable: true, width: '120px', class: 'producer-col', cellClass: 'producer-col' },
          { value: 'solicitor', text: 'Solicitor', align: 'left', sortable: true, width: '120px', class: 'solicitor-col', cellClass: 'solicitor-col' }
        ];

        if (this.showActionsColumn) {
          headers.unshift({
            value: 'actions',
            text: 'Actions',
            align: 'center',
            sortable: false,
            width: '90px',
            class: 'actions-col',
            cellClass: 'actions-col'
          });
        }

        return headers;
      },

      reportFilterSummary() {
        const selectedStatus = this.statusOptions.find(({ value }) => value === this.status);
        const parts = [
          `Status: ${selectedStatus ? selectedStatus.text : 'Book'}`,
          `Producer: ${this.producer || 'Not selected'}`
        ];

        if (this.printSortSummary) parts.push(this.printSortSummary);
        return parts.join(' | ');
      },

      printMonthFilterSummary() {
        if (!this.isExpiryMonthFilterActive) return '';
        return `Filtered to Expiry Months: ${this.selectedExpiryMonthsSummary}`;
      },

      printSortSummary() {
        const sortKeys = Array.isArray(this.sortBy) ? this.sortBy.filter(Boolean) : (this.sortBy ? [this.sortBy] : []);
        const sortDirections = Array.isArray(this.sortDesc) ? this.sortDesc : [this.sortDesc];
        if (!sortKeys.length) return '';

        const expirySortKeys = ['expiryYearSortValue', 'expiryMonthSortValue', 'expiryDaySortValue'];
        const defaultExpirySortDirections = [false, false, false];
        const usesDefaultExpirySort = expirySortKeys.every((key, index) => {
          return sortKeys[index] === key && !!sortDirections[index] === defaultExpirySortDirections[index];
        });
        if (usesDefaultExpirySort) return 'Sorted by Expiry';

        const parts = sortKeys.map((key, index) => {
          const label = this.getSortLabel(key);
          const direction = this.getSortDirectionLabel(key, !!sortDirections[index]);
          return `${label} (${direction})`;
        });

        return `Sorted by ${parts.join(', then ')} on this page`;
      },

      printPageRowsSummary() {
        if (!this.renewalData.length) return '';

        const currentPageRows = this.isExpiryMonthFilterActive ? this.filteredRenewalData.length : this.renewalData.length;
        const totalMatchingRows = Math.max(Number(this.renewalTotalCount) || 0, currentPageRows);
        return `Report ${this.renewalPageSummary} | Rows printed: ${this.formatCount(currentPageRows)} on this server page | ${this.formatCount(totalMatchingRows)} total matching rows`;
      },

      printPaginationClarifier() {
        return 'Rows per Page controls report pages. Printed page count is separate and depends on browser and printer settings.';
      },

      renewalPageCount() {
        const totalRecords = Math.max(Number(this.renewalTotalCount) || 0, 0);
        const perPage = Math.max(Number(this.itemsPerPage) || 0, 0);
        if (!totalRecords || !perPage) return 1;
        return Math.max(Math.ceil(totalRecords / perPage), 1);
      },

      renewalPageSummary() {
        const currentPage = Math.max(Number(this.currentPage) || 1, 1);
        return `Page ${currentPage} of ${this.renewalPageCount}`;
      },

      renewalPageRangeSummary() {
        const totalRecords = Math.max(Number(this.renewalTotalCount) || 0, 0);
        if (!totalRecords || !this.renewalData.length) return `0-0 of ${this.formatCount(totalRecords)}`;

        const currentPage = Math.max(Number(this.currentPage) || 1, 1);
        const perPage = Math.max(Number(this.itemsPerPage) || 1, 1);
        const start = ((currentPage - 1) * perPage) + 1;
        const end = Math.min(start + this.renewalData.length - 1, totalRecords);
        return `${this.formatCount(start)}-${this.formatCount(end)} of ${this.formatCount(totalRecords)}`;
      },

      hasPreviousRenewalPage() {
        return Math.max(Number(this.currentPage) || 1, 1) > 1;
      },

      hasNextRenewalPage() {
        return Math.max(Number(this.currentPage) || 1, 1) < this.renewalPageCount;
      },

      renewalTableHeight() {
        const breakpoint = this.$vuetify && this.$vuetify.breakpoint;
        const isSmallScreen = breakpoint && breakpoint.smAndDown;
        const minHeight = isSmallScreen ? 250 : 320;
        const measuredReservedHeight = Number(this.renewalTableViewportTop) + Number(this.renewalTableBottomReservedHeight);

        if (measuredReservedHeight > 0) {
          return `${Math.max(this.viewportHeight - measuredReservedHeight, minHeight)}px`;
        }

        const fallbackReservedHeight = isSmallScreen
          ? (this.showRenewalTotalsBand ? 500 : 420)
          : (this.showRenewalTotalsBand ? 450 : 360);
        return `${Math.max(this.viewportHeight - fallbackReservedHeight, minHeight)}px`;
      },

    },

    watch: {
      selectedExpiryMonths(value, previousValue) {
        const normalizedSelection = this.normalizeExpiryMonthSelection(value, previousValue);
        if (!this.areArrayValuesEqual(value, normalizedSelection)) {
          this.selectedExpiryMonths = normalizedSelection;
          return;
        }

        this.$nextTick(() => {
          this.scheduleExpiryMonthFieldLayoutSync();
          if (this.searchResultLoadedShow && !this.loading) {
            this.currentPage = 1;
            this.invalidateResults();
          }
        });
      },

      producer() {
        if (this.producer) this.showProducerRequiredHint = false;
        this.showProducerInvalidHint = false;
        this.invalidateResults();
      },

      status() {
        if (this.status) this.showStatusRequiredHint = false;
        this.showStatusInvalidHint = false;
        this.invalidateResults();
      }
    },

    created() {
      document.title = 'Renewal Report';
      this.currentUsername = this.$store.getters.username || '';
      this.loadProducerOptions();
    },

    mounted() {
      this.updateViewportHeight();
      window.addEventListener('resize', this.updateViewportHeight);
      this.$nextTick(() => {
        this.initializeExpiryMonthFieldResizeObserver();
        this.scheduleExpiryMonthFieldLayoutSync();
        this.scheduleRenewalTableLayoutSync();
      });
    },

    updated() {
      this.scheduleRenewalTableLayoutSync();
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.updateViewportHeight);
      this.teardownExpiryMonthFieldResizeObserver();
      this.teardownRenewalTableLayoutSync();
    },

    methods: {
      updateViewportHeight() {
        if (typeof window === 'undefined') return;
        this.viewportHeight = window.innerHeight;
        this.scheduleExpiryMonthFieldLayoutSync();
        this.scheduleRenewalTableLayoutSync();
      },

      getRenewalTableRoot() {
        const table = this.$refs.renewalTable;
        return table && table.$el ? table.$el : null;
      },

      getRenewalLayoutElement(refName) {
        const element = this.$refs[refName];
        if (!element) return null;
        return element.$el ? element.$el : element;
      },

      getRenewalLayoutElementOuterHeight(refName) {
        if (typeof window === 'undefined') return 0;

        const element = this.getRenewalLayoutElement(refName);
        if (!element || element.offsetParent === null) return 0;

        const styles = window.getComputedStyle(element);
        const marginTop = parseFloat(styles.marginTop) || 0;
        const marginBottom = parseFloat(styles.marginBottom) || 0;
        return element.offsetHeight + marginTop + marginBottom;
      },

      syncRenewalTableLayout() {
        if (typeof window === 'undefined') return;

        const tableRoot = this.getRenewalTableRoot();
        if (!tableRoot || this.loading || !this.searchResultLoadedShow) {
          if (this.renewalTableViewportTop !== 0) this.renewalTableViewportTop = 0;
          if (this.renewalTableBottomReservedHeight !== 0) this.renewalTableBottomReservedHeight = 0;
          return;
        }

        const footerHeight = this.getRenewalLayoutElementOuterHeight('renewalTableFooter');
        const totalsBandHeight = this.getRenewalLayoutElementOuterHeight('renewalTotalsBand');
        const viewportTop = Math.max(Math.round(tableRoot.getBoundingClientRect().top), 0);
        const bottomReservedHeight = Math.max(
          Math.round(footerHeight + totalsBandHeight),
          0
        );

        if (this.renewalTableViewportTop !== viewportTop) this.renewalTableViewportTop = viewportTop;
        if (this.renewalTableBottomReservedHeight !== bottomReservedHeight) {
          this.renewalTableBottomReservedHeight = bottomReservedHeight;
        }
      },

      scheduleRenewalTableLayoutSync() {
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
          this.syncRenewalTableLayout();
          return;
        }

        if (this._renewalTableLayoutFrame) window.cancelAnimationFrame(this._renewalTableLayoutFrame);
        this._renewalTableLayoutFrame = window.requestAnimationFrame(() => {
          this._renewalTableLayoutFrame = null;
          this.syncRenewalTableLayout();
        });
      },

      teardownRenewalTableLayoutSync() {
        if (this._renewalTableLayoutFrame) {
          window.cancelAnimationFrame(this._renewalTableLayoutFrame);
          this._renewalTableLayoutFrame = null;
        }
      },

      getExpiryMonthFieldRoot() {
        const searchInput = this.$refs.expiryMonthsInput;
        const field = searchInput && searchInput.$refs ? searchInput.$refs.field : null;
        return field && field.$el ? field.$el : null;
      },

      syncExpiryMonthFieldLayout() {
        const root = this.getExpiryMonthFieldRoot();
        if (!root) {
          this.expiryMonthChipRowCount = 1;
          return;
        }

        const selections = root.querySelector('.v-select__selections');
        if (!selections) {
          this.expiryMonthChipRowCount = 1;
          return;
        }

        const chips = Array.from(selections.querySelectorAll('.v-chip'))
          .filter((chip) => chip && chip.offsetParent !== null);

        if (!chips.length) {
          this.expiryMonthChipRowCount = 1;
          return;
        }

        const rowCount = new Set(chips.map((chip) => Math.round(chip.offsetTop))).size;
        this.expiryMonthChipRowCount = Math.min(Math.max(rowCount, 1), 4);
      },

      scheduleExpiryMonthFieldLayoutSync() {
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
          this.syncExpiryMonthFieldLayout();
          return;
        }

        if (this._expiryMonthFieldLayoutFrame) window.cancelAnimationFrame(this._expiryMonthFieldLayoutFrame);
        this._expiryMonthFieldLayoutFrame = window.requestAnimationFrame(() => {
          this._expiryMonthFieldLayoutFrame = null;
          this.syncExpiryMonthFieldLayout();
        });
      },

      initializeExpiryMonthFieldResizeObserver() {
        if (typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') return;

        const root = this.getExpiryMonthFieldRoot();
        if (!root) return;

        this.teardownExpiryMonthFieldResizeObserver();
        this._expiryMonthFieldResizeObserver = new window.ResizeObserver(() => {
          this.scheduleExpiryMonthFieldLayoutSync();
        });
        this._expiryMonthFieldResizeObserver.observe(root);
      },

      teardownExpiryMonthFieldResizeObserver() {
        if (this._expiryMonthFieldResizeObserver) {
          this._expiryMonthFieldResizeObserver.disconnect();
          this._expiryMonthFieldResizeObserver = null;
        }

        if (this._expiryMonthFieldLayoutFrame) {
          window.cancelAnimationFrame(this._expiryMonthFieldLayoutFrame);
          this._expiryMonthFieldLayoutFrame = null;
        }
      },

      getSortLabel(key) {
        const sortLabels = {
          expiryYearSortValue: 'xYr',
          expiryMonthSortValue: 'xMo',
          expiryDaySortValue: 'xDy'
        };
        if (sortLabels[key]) return sortLabels[key];
        const header = this.renewalHeaders.find((item) => item.value === key);
        return header ? header.text : key.replace(/SortValue$/, '');
      },

      getSortDirectionLabel(key, isDesc) {
        const dateSorts = {
          expiryYearSortValue: ['earliest year first', 'latest year first'],
          expiryMonthSortValue: ['lowest month first', 'highest month first'],
          expiryDaySortValue: ['lowest day first', 'highest day first']
        };
        if (dateSorts[key]) return dateSorts[key][isDesc ? 1 : 0];
        if (key === 'premiumValue') return isDesc ? 'highest first' : 'lowest first';
        return isDesc ? 'Z-A' : 'A-Z';
      },

      getExpiryMonthSelectionText(item) {
        if (item && item.value === ALL_MONTHS_VALUE) return null;
        if (item && item.shortText) return item.shortText;
        if (item && item.text) return item.text;
        return '';
      },

      getNormalizedExpiryMonthValues(values = this.selectedExpiryMonths) {
        if (!Array.isArray(values)) return [];

        return Array.from(new Set(
          values
            .map((value) => Number(value))
            .filter((value) => EXPIRY_MONTH_VALUES.includes(value))
        )).sort((left, right) => left - right);
      },

      getAllExpiryMonthsSelection() {
        return [ALL_MONTHS_VALUE, ...EXPIRY_MONTH_VALUES];
      },

      areArrayValuesEqual(left = [], right = []) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
        return left.every((value, index) => value === right[index]);
      },

      normalizeExpiryMonthSelection(value, previousValue = []) {
        const previousHadAllMonths = Array.isArray(previousValue) && previousValue.includes(ALL_MONTHS_VALUE);
        const nextHadAllMonths = Array.isArray(value) && value.includes(ALL_MONTHS_VALUE);
        const selectedMonths = this.getNormalizedExpiryMonthValues(value);
        const everyMonthSelected = selectedMonths.length === EXPIRY_MONTH_VALUES.length;

        if (nextHadAllMonths && !previousHadAllMonths) return this.getAllExpiryMonthsSelection();
        if (previousHadAllMonths && !nextHadAllMonths) return everyMonthSelected ? [] : selectedMonths;
        if (nextHadAllMonths && previousHadAllMonths && !everyMonthSelected) return selectedMonths;
        if (!nextHadAllMonths && !previousHadAllMonths && everyMonthSelected) return this.getAllExpiryMonthsSelection();
        if (nextHadAllMonths && previousHadAllMonths && everyMonthSelected) return this.getAllExpiryMonthsSelection();
        return selectedMonths;
      },

      getDefaultProducer(options = this.producerOptions) {
        return Array.isArray(options) ? (options.find((username) => username === this.currentUsername) || '') : '';
      },

      normalizeForMatch(value) {
        return String(value === null || value === undefined ? '' : value).trim().toLocaleLowerCase();
      },

      getStatusOptionText(option) {
        return option && option.text ? option.text : '';
      },

      getStatusOptionValue(option) {
        return option && option.value ? option.value : '';
      },

      getProducerOptionText(option) {
        return option || '';
      },

      getProducerOptionValue(option) {
        return option || '';
      },

      focusSearchField(refName) {
        const input = this.$refs[refName];
        if (input && typeof input.focus === 'function') {
          input.focus();
          return;
        }

        const field = input && input.$refs ? input.$refs.input : null;
        if (field && typeof field.focus === 'function') field.focus();
      },

      getSearchField(refName) {
        return this.$refs[refName] || null;
      },

      closeSearchField(refName) {
        const input = this.getSearchField(refName);
        if (!input) return;

        const fieldComponent = input.$refs && input.$refs.field ? input.$refs.field : input;

        if (fieldComponent.isMenuActive !== undefined) fieldComponent.isMenuActive = false;
        if (typeof fieldComponent.blur === 'function') fieldComponent.blur();

        const field = fieldComponent.$refs ? fieldComponent.$refs.input : null;
        if (field && typeof field.blur === 'function') field.blur();
      },

      closeRenewalSearchMenus() {
        this.closeSearchField('expiryMonthsInput');
        this.closeSearchField('statusInput');
        this.closeSearchField('producerInput');
      },

      getSearchFieldValue(refName, fallbackValue = '') {
        const input = this.getSearchField(refName);
        if (input && input.$refs && input.$refs.input && typeof input.$refs.input.value === 'string') {
          return input.$refs.input.value;
        }

        if (input && input.internalSearch !== undefined) {
          return input.internalSearch;
        }

        return fallbackValue;
      },

      findAutocompleteMatch(items, searchValue, getItemText, getItemValue) {
        const normalizedSearch = this.normalizeForMatch(searchValue);
        if (!normalizedSearch) return null;

        return items.find((item) => {
          const textMatch = this.normalizeForMatch(getItemText(item)) === normalizedSearch;
          const valueMatch = this.normalizeForMatch(getItemValue(item)) === normalizedSearch;
          return textMatch || valueMatch;
        }) || null;
      },

      getSelectedAutocompleteItem(items, selectedValue, getItemValue) {
        const normalizedValue = this.normalizeForMatch(selectedValue);
        if (!normalizedValue) return null;

        return items.find((item) => this.normalizeForMatch(getItemValue(item)) === normalizedValue) || null;
      },

      getAutocompleteSelectionTokens(items, selectedValue, getItemText, getItemValue) {
        const tokens = new Set();
        const addToken = (value) => {
          const normalizedValue = this.normalizeForMatch(value);
          if (normalizedValue) tokens.add(normalizedValue);
        };

        addToken(selectedValue);
        const selectedItem = this.getSelectedAutocompleteItem(items, selectedValue, getItemValue);
        if (selectedItem) {
          addToken(getItemText(selectedItem));
          addToken(getItemValue(selectedItem));
        }

        return tokens;
      },

      commitAutocompleteSelection(refName, fallbackSearchValue, items, getItemText, getItemValue, setValue) {
        const input = this.getSearchField(refName);
        const searchValue = this.getSearchFieldValue(refName, fallbackSearchValue);
        const match = this.findAutocompleteMatch(items, searchValue, getItemText, getItemValue);
        if (!match) return false;

        if (input && typeof input.selectItem === 'function') {
          input.selectItem(match);
        } else {
          setValue(getItemValue(match));
        }

        return true;
      },

      getUnresolvedAutocompleteValue(refName, fallbackSearchValue, items, selectedValue, getItemText, getItemValue) {
        const typedValue = this.getSearchFieldValue(refName, fallbackSearchValue);
        const normalizedSearch = this.normalizeForMatch(typedValue);
        const selectionTokens = this.getAutocompleteSelectionTokens(items, selectedValue, getItemText, getItemValue);

        if (!normalizedSearch && !selectionTokens.size) return null;

        const match = this.findAutocompleteMatch(items, typedValue, getItemText, getItemValue);
        if (match) return null;

        if (selectionTokens.has(normalizedSearch)) return null;

        return typedValue;
      },

      getUnresolvedSearchValue(refName) {
        if (refName === 'statusInput') {
          return this.getUnresolvedAutocompleteValue(
            'statusInput',
            this.statusSearch,
            this.statusOptions,
            this.status,
            this.getStatusOptionText,
            this.getStatusOptionValue
          );
        }

        if (refName === 'producerInput') {
          return this.getUnresolvedAutocompleteValue(
            'producerInput',
            this.producerSearch,
            this.producerOptions,
            this.producer,
            this.getProducerOptionText,
            this.getProducerOptionValue
          );
        }

        return null;
      },

      async commitPendingSearchSelections() {
        const committedStatus = this.commitAutocompleteSelection(
          'statusInput',
          this.statusSearch,
          this.statusOptions,
          this.getStatusOptionText,
          this.getStatusOptionValue,
          (value) => {
            this.status = value;
          }
        );
        const committedProducer = this.commitAutocompleteSelection(
          'producerInput',
          this.producerSearch,
          this.producerOptions,
          this.getProducerOptionText,
          this.getProducerOptionValue,
          (value) => {
            this.producer = value;
          }
        );

        if (committedStatus || committedProducer) await this.$nextTick();
      },

      invalidateResults() {
        this.searchResultLoadedShow = false;
        this.showPrintDialog = false;
      },

      resetResults() {
        this.invalidateResults();
        this.currentPage = 1;
        this.renewalData = [];
        this.renewalServerFooters = null;
        this.renewalTotalCount = 0;
      },

      async getRenewal() {
        await this.commitPendingSearchSelections();

        const unresolvedStatusValue = this.getUnresolvedSearchValue('statusInput');
        if (unresolvedStatusValue !== null) {
          this.handleInvalidStatusEnter({ value: unresolvedStatusValue });
          return;
        }

        const unresolvedProducerValue = this.getUnresolvedSearchValue('producerInput');
        if (unresolvedProducerValue !== null) {
          this.handleInvalidProducerEnter({ value: unresolvedProducerValue });
          return;
        }

        if (!this.status) {
          this.showStatusInvalidHint = false;
          this.showStatusRequiredHint = true;
          this.$nextTick(() => this.focusSearchField('statusInput'));
          return;
        }

        if (!this.producer) {
          this.showProducerInvalidHint = false;
          this.showProducerRequiredHint = true;
          this.$nextTick(() => this.focusSearchField('producerInput'));
          return;
        }

        this.showStatusInvalidHint = false;
        this.showStatusRequiredHint = false;
        this.showProducerInvalidHint = false;
        this.showProducerRequiredHint = false;
        this.closeRenewalSearchMenus();
        this.resetResults();

        await this.fetchRenewalPage(1, this.itemsPerPage);
      },

      async getRenewalAfterSelectionSettles() {
        this.closeSearchField('expiryMonthsInput');
        await this.$nextTick();
        await new Promise((resolve) => setTimeout(resolve, 150));
        return this.getRenewal();
      },

      buildRenewalRequestKey(page = this.currentPage, perPage = this.itemsPerPage) {
        const monthKey = this.isExpiryMonthFilterActive
          ? this.getNormalizedExpiryMonthValues(this.selectedExpiryMonths).join(',')
          : '';
        return [this.status, this.producer, page, perPage, monthKey].join('|');
      },

      parseRenewalPageCandidate(candidate) {
        const numericValue = Number(candidate);
        return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : null;
      },

      getRenewalCurrentPage(renewals, fallbackPage = 1) {
        const candidates = [
          renewals?.currentPage,
          renewals?.current_page,
          renewals?.page,
          renewals?.data?.currentPage,
          renewals?.data?.current_page,
          renewals?.data?.page,
          renewals?.result?.currentPage,
          renewals?.result?.current_page,
          renewals?.result?.page,
          renewals?.pageInfo?.currentPage,
          renewals?.pageInfo?.current_page,
          renewals?.pagination?.page,
          renewals?.meta?.page
        ];

        for (const candidate of candidates) {
          const numericValue = this.parseRenewalPageCandidate(candidate);
          if (numericValue !== null) return numericValue;
        }

        return Math.max(Number(fallbackPage) || 1, 1);
      },

      async fetchRenewalPage(page = this.currentPage, perPage = this.itemsPerPage) {
        const targetPage = Math.max(Number(page) || 1, 1);
        const targetItemsPerPage = Math.max(Number(perPage) || DEFAULT_RENEWAL_PER_PAGE, 1);
        const requestKey = this.buildRenewalRequestKey(targetPage, targetItemsPerPage);

        if (this.loading && requestKey === this.activeRenewalRequestKey) return;

        this.loading = true;
        this.activeRenewalRequestKey = requestKey;

        try {
          const expiryMonths = this.getNormalizedExpiryMonthValues(this.selectedExpiryMonths);
          const expiryMonth = this.isExpiryMonthFilterActive
            ? (expiryMonths.length === 1 ? expiryMonths[0] : expiryMonths)
            : undefined;

          const renewals = await reportsAPIService.getRenewalReports({
            status: this.status,
            producers: this.producer,
            expiryMonth,
            page: targetPage,
            perPage: targetItemsPerPage
          }, this.$router);

          if (this.activeRenewalRequestKey !== requestKey) return;

          this.currentPage = this.getRenewalCurrentPage(renewals, targetPage);
          this.itemsPerPage = targetItemsPerPage;
          this.renewalData = this.normalizeRenewalRows(renewals);
          this.renewalTotalCount = this.getRenewalTotalCount(renewals, this.renewalData.length);
          const footers = renewals && renewals.footers && typeof renewals.footers === 'object' ? renewals.footers : null;
          this.renewalServerFooters = (this.isBookRenewalQuery && footers) ? footers : null;
          this.searchResultLoadedShow = true;
        } catch (e) {
          if (this.activeRenewalRequestKey !== requestKey) return;

          this.$refs.errorDialog.showError({
            errorMessage: "Failed to get renewal data",
            forDevelopers: e
          });
        } finally {
          if (this.activeRenewalRequestKey === requestKey) {
            this.loading = false;
            this.activeRenewalRequestKey = '';
          }
        }
      },

      extractRenewalRows(renewals) {
        if (renewals && typeof renewals === 'object' && Array.isArray(renewals.data)) {
          return renewals.data;
        }

        if (Array.isArray(renewals)) {
          return renewals;
        }

        const arrayCandidates = [
          renewals,
          renewals?.result,
          renewals?.results,
          renewals?.items,
          renewals?.rows,
          renewals?.records,
          renewals?.data,
          renewals?.payload,
          renewals?.pageInfo?.items,
          renewals?.pageInfo?.rows,
          renewals?.pagination?.items,
          renewals?.pagination?.rows,
          renewals?.result?.items,
          renewals?.result?.rows,
          renewals?.result?.results,
          renewals?.result?.data,
          renewals?.data?.items,
          renewals?.data?.rows,
          renewals?.data?.results,
          renewals?.data?.records,
          renewals?.payload?.items,
          renewals?.payload?.rows
        ];

        for (const candidate of arrayCandidates) {
          if (Array.isArray(candidate)) return candidate;
        }

        const nestedObjects = [
          renewals?.result,
          renewals?.data,
          renewals?.payload,
          renewals?.pageInfo,
          renewals?.pagination
        ].filter((candidate) => candidate && typeof candidate === 'object');

        for (const container of nestedObjects) {
          const firstArray = Object.values(container).find((value) => Array.isArray(value));
          if (Array.isArray(firstArray)) return firstArray;
        }

        return [];
      },

      normalizeRenewalRows(renewals) {
        const rows = this.extractRenewalRows(renewals);

        const normalizedRows = rows
          .map((row) => {
            const expiryRaw = row && row.expiry ? row.expiry : '';
            const expiryParts = this.getDateParts(expiryRaw);
            const placementRaw = row?.is_new ?? row?.placement ?? row?.renewal;
            const statusRaw = row?.status || row?.policy_status || row?.renewal_status || '';
            const producer = row && (row._producer || row.producer || row.prodname || row.prod_name)
              ? row._producer || row.producer || row.prodname || row.prod_name
              : '';
            const solicitor = row && (row._solicitor || row.solicitor || row.subname)
              ? row._solicitor || row.solicitor || row.subname
              : this.getCidLabel(row?.subid);

            return {
              ...row,
              insured: row?.insured || '',
              carrier: row?.carrier || '',
              type: row?.type || '',
              status: this.formatStatus(statusRaw),
              is_new: this.formatPlacement(placementRaw),
              expiryMonth: expiryParts.month,
              expiryMonthDisplay: this.formatDatePart(expiryParts.month),
              expiryMonthSortValue: this.buildDatePartSortValue(expiryParts.month),
              expiryDayDisplay: this.formatDatePart(expiryParts.day),
              expiryDaySortValue: this.buildDatePartSortValue(expiryParts.day),
              expiryYearDisplay: this.formatDatePart(expiryParts.year, 4),
              expiryYearSortValue: this.buildDatePartSortValue(expiryParts.year),
              premium: this.formatCurrency(row?.premium),
              premiumValue: this.buildCurrencySortValue(row?.premium),
              producer,
              solicitor
            };
          });

        return normalizedRows;
      },

      getCidLabel(value) {
        if (!value || !this.$_lists || !Array.isArray(this.$_lists.cid)) return '';
        const numericValue = Number(value);
        const match = this.$_lists.cid.find((item) => Number(item.value || item.cid) === numericValue);
        return match ? (match.sort || match.text || '') : '';
      },

      formatCurrency(value) {
        if (value === null || value === undefined || `${value}`.trim() === '') return '';
        return currency(value).format();
      },

      buildCurrencySortValue(value) {
        if (value === null || value === undefined || `${value}`.trim() === '') return Number.NEGATIVE_INFINITY;
        return currency(value).value;
      },

      compareRenewalSortValues(leftValue, rightValue) {
        if (leftValue === rightValue) return 0;

        const leftIsMissing = leftValue === null || leftValue === undefined;
        const rightIsMissing = rightValue === null || rightValue === undefined;
        if (leftIsMissing || rightIsMissing) {
          if (leftIsMissing && rightIsMissing) return 0;
          return leftIsMissing ? 1 : -1;
        }

        if (typeof leftValue === 'number' && typeof rightValue === 'number') {
          return leftValue - rightValue;
        }

        return String(leftValue).localeCompare(String(rightValue), undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      },

      getRenewalTotalsBucket(value) {
        const normalized = String(value === null || value === undefined ? '' : value).trim().toLowerCase();

        if (['new', 'new biz', 'new business'].includes(normalized)) return 'newBiz';
        if (normalized === 'recovered' || normalized.startsWith('recover')) return 'recovered';
        if (normalized === 'renewal' || normalized === 'renew') return 'renewal';
        return 'other';
      },

      parseFooterPremiumValue(value) {
        if (value === null || value === undefined || value === '') return 0;
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        return currency(String(value)).value;
      },

      renewalTotalsSectionsFromApiFooters(footers) {
        const mapRow = (apiKey, key, label) => {
          const row = footers[apiKey];
          return {
            key,
            label,
            count: row ? Number(row.count) || 0 : 0,
            premium: row ? this.parseFooterPremiumValue(row.premium) : 0
          };
        };

        return [
          mapRow('New Biz', 'new-biz', 'New Biz'),
          mapRow('Recovered', 'recovered', 'Recovered'),
          mapRow('Renewal', 'renewal', 'Renewal'),
          mapRow('Other', 'other', 'Other'),
          mapRow('Total', 'total', 'Total')
        ];
      },

      formatCount(value) {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue.toLocaleString('en-US') : '0';
      },

      formatLoadedRenewalCountSummary(loadedCount, totalCount) {
        const safeLoadedCount = Math.max(Number(loadedCount) || 0, 0);
        const safeTotalCount = Math.max(Number(totalCount) || 0, safeLoadedCount);
        if (!safeLoadedCount && !safeTotalCount) return this.formatCount(safeLoadedCount);
        return `${this.formatCount(safeLoadedCount)} of ${this.formatCount(safeTotalCount)}`;
      },

      parseRenewalTotalCountCandidate(candidate) {
        if (candidate === null || candidate === undefined) return null;

        const normalizedCandidate = String(candidate).trim();
        if (!normalizedCandidate) return null;

        const contentRangeMatch = normalizedCandidate.match(/\/\s*([\d,]+)\s*$/);
        if (contentRangeMatch) {
          const rangeTotal = Number(contentRangeMatch[1].replace(/,/g, ''));
          if (Number.isFinite(rangeTotal) && rangeTotal >= 0) return rangeTotal;
        }

        const numericValue = Number(normalizedCandidate.replace(/,/g, ''));
        return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null;
      },

      getRenewalTotalCount(renewals, fallbackCount = 0) {
        const responseHeaders = renewals && renewals.__responseHeaders && typeof renewals.__responseHeaders === 'object'
          ? renewals.__responseHeaders
          : {};
        const candidates = [
          renewals?.count,
          renewals?.total,
          renewals?.totalRecords,
          renewals?.total_records,
          renewals?.totalCount,
          renewals?.total_count,
          renewals?.recordsTotal,
          renewals?.records_total,
          renewals?.data?.count,
          renewals?.data?.total,
          renewals?.data?.totalCount,
          renewals?.data?.total_count,
          renewals?.result?.count,
          renewals?.result?.total,
          renewals?.result?.totalCount,
          renewals?.result?.total_count,
          renewals?.pageInfo?.totalRecords,
          renewals?.pageInfo?.total_records,
          renewals?.pagination?.total,
          renewals?.pagination?.count,
          renewals?.meta?.total,
          renewals?.meta?.count,
          renewals?.resultCount,
          renewals?.result_count,
          responseHeaders['x-total-count'],
          responseHeaders['x-total'],
          responseHeaders['total-count'],
          responseHeaders['content-range'],
          responseHeaders['content_range'],
          responseHeaders['x-pagination-total'],
          responseHeaders['x-pagination-count'],
          responseHeaders.count
        ];

        for (const candidate of candidates) {
          const numericValue = this.parseRenewalTotalCountCandidate(candidate);
          if (numericValue !== null) {
            return numericValue;
          }
        }

        return Math.max(Number(fallbackCount) || 0, 0);
      },

      buildDatePartSortValue(value) {
        return value === null || value === undefined ? Number.MAX_SAFE_INTEGER : Number(value);
      },

      formatPlacement(value) {
        if (value === true) return 'Renewal';
        if (value === false) return 'New';

        const normalized = String(value === null || value === undefined ? '' : value).trim().toLowerCase();
        if (!normalized) return '';
        if (['1', 'true', 'renewal', 'renew', 'r'].includes(normalized)) return 'Renewal';
        if (['0', 'false', 'new', 'new business', 'n'].includes(normalized)) return 'New';
        return this.titleCase(normalized);
      },

      formatReportDate(value) {
        const normalized = String(value === null || value === undefined ? '' : value).trim();
        if (!normalized || this.isSentinelDateValue(normalized)) return '';
        if (/^\d{2}-\d{2}-\d{4}$/.test(normalized)) return normalized;

        const ymdMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (ymdMatch) return `${ymdMatch[2]}-${ymdMatch[3]}-${ymdMatch[1]}`;

        const parsed = new Date(normalized);
        if (Number.isNaN(parsed.getTime())) return normalized;

        const mm = String(parsed.getMonth() + 1).padStart(2, '0');
        const dd = String(parsed.getDate()).padStart(2, '0');
        const yyyy = parsed.getFullYear();
        return `${mm}-${dd}-${yyyy}`;
      },

      formatDatePart(value, minLength = 2) {
        if (value === null || value === undefined) return '';
        return String(value).padStart(minLength, '0');
      },

      formatStatus(value) {
        const normalized = String(value === null || value === undefined ? '' : value).trim().toLowerCase();
        if (!normalized) return '';
        const match = this.statusOptions.find((option) => option.value === normalized);
        return match ? match.text : this.titleCase(normalized);
      },

      titleCase(value) {
        return String(value === null || value === undefined ? '' : value)
          .trim()
          .split(/[\s_]+/)
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
          .join(' ');
      },

      isSentinelDateValue(value) {
        return /^0001-01-01(?:[T\s].*)?$/.test(value) || /^01-01-0001(?:\s.*)?$/.test(value);
      },

      buildDateSortValue(dateValue) {
        const value = String(dateValue === null || dateValue === undefined ? '' : dateValue).trim();
        if (!value || this.isSentinelDateValue(value)) return Number.MAX_SAFE_INTEGER;

        const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (ymdMatch) return Date.UTC(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));

        const mdyMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (mdyMatch) return Date.UTC(Number(mdyMatch[3]), Number(mdyMatch[1]) - 1, Number(mdyMatch[2]));

        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? Number.MAX_SAFE_INTEGER : parsed.getTime();
      },

      getDateParts(dateValue) {
        const value = String(dateValue === null || dateValue === undefined ? '' : dateValue).trim();
        if (!value || this.isSentinelDateValue(value)) {
          return { month: null, day: null, year: null };
        }

        const ymdMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (ymdMatch) {
          return {
            year: Number(ymdMatch[1]),
            month: Number(ymdMatch[2]),
            day: Number(ymdMatch[3])
          };
        }

        const mdyMatch = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (mdyMatch) {
          return {
            month: Number(mdyMatch[1]),
            day: Number(mdyMatch[2]),
            year: Number(mdyMatch[3])
          };
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
          return { month: null, day: null, year: null };
        }

        return {
          month: parsed.getMonth() + 1,
          day: parsed.getDate(),
          year: parsed.getFullYear()
        };
      },

      async loadProducerOptions() {
        try {
          const data = await usersAPIService.getUsernames(this.$router);
          const options = new Set();

          const rows = Array.isArray(data) ? data : [];
          for (const row of rows) {
            const username = typeof row === 'string' ? row : row && row.username;
            if (username) options.add(username);
          }

          this.producerOptions = Array.from(options).sort((left, right) => left.localeCompare(right));
          const defaultProducer = this.getDefaultProducer(this.producerOptions);
          if (!this.producer || !this.producerOptions.includes(this.producer)) this.producer = defaultProducer;
        } catch (e) {
          this.producerOptions = [];
          this.producer = '';
          this.$_showErrorDialog({
            errorMessage: "Failed to get producers.",
            forDevelopers: e
          });
        }
      },

      handleInvalidStatusEnter({ value }) {
        const typedValue = String(value === null || value === undefined ? '' : value).trim();
        this.showStatusRequiredHint = !typedValue;
        this.showStatusInvalidHint = !!typedValue;
        this.$nextTick(() => this.focusSearchField('statusInput'));
      },

      handleInvalidProducerEnter({ value }) {
        const typedValue = String(value === null || value === undefined ? '' : value).trim();
        this.showProducerRequiredHint = !typedValue;
        this.showProducerInvalidHint = !!typedValue;
        this.$nextTick(() => this.focusSearchField('producerInput'));
      },

      onRenewalPageChange(page) {
        if (!this.searchResultLoadedShow) return;
        this.fetchRenewalPage(page, this.itemsPerPage);
      },

      onRenewalItemsPerPageChange(itemsPerPage) {
        const nextItemsPerPage = Math.max(Number(itemsPerPage) || DEFAULT_RENEWAL_PER_PAGE, 1);
        this.itemsPerPage = nextItemsPerPage;
        this.currentPage = 1;

        if (!this.searchResultLoadedShow) return;
        this.fetchRenewalPage(1, nextItemsPerPage);
      },

      clearExpiryMonths() {
        this.selectedExpiryMonths = [];
        this.closeSearchField('expiryMonthsInput');
      },

      clearSearch() {
        this.loading = false;
        this.activeRenewalRequestKey = '';
        this.currentPage = 1;
        this.itemsPerPage = DEFAULT_RENEWAL_PER_PAGE;
        this.producer = this.getDefaultProducer();
        this.status = 'book';
        this.selectedExpiryMonths = [];
        this.showProducerInvalidHint = false;
        this.showProducerRequiredHint = false;
        this.showStatusInvalidHint = false;
        this.showStatusRequiredHint = false;
        this.resetResults();
      },

      printDiv() {
        this.showPrintDialog = true;
      },

      confirmPrint() {
        this.showPrintDialog = false;
        window.print();
      }
    }
  }
</script>
<style>
  .print-settings-alert {
    color: #b71c1c;
    font-weight: 700;
  }

  /* 7 May 2026 JCH: Match former body-1; avoid Vuetify .v-application .body-1 (wins over single-class rules). */
  .renewal-info-dialog {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5rem;
    letter-spacing: 0.03125em;
  }

  @media (max-width: 600px) {
    .v-application .v-dialog .renewal-info-dialog,
    .v-application .v-dialog .renewal-info-dialog p {
      font-size: 0.8rem !important;
      line-height: 1 !important;
    }
  }

  .renewal-toolbar {
    gap: 8px 12px;
  }

  .renewal-toolbar__primary,
  .renewal-toolbar__secondary {
    min-width: 0;
  }

  .renewal-toolbar__secondary {
    gap: 12px;
  }

  .renewal-filter-group {
    width: 100%;
    padding-left: 2px;
    padding-right: 2px;
  }

  .renewal-filter-col--months .renewal-filter-group {
    transition: transform 150ms ease;
  }

  .renewal-filter-input {
    width: 100%;
  }

  .renewal-expiry-months-input ::v-deep .v-input__slot {
    min-height: 64px !important;
    align-items: flex-start !important;
    padding-top: 3px !important;
    padding-bottom: 5px !important;
  }

  .renewal-expiry-months-input ::v-deep .v-select__slot {
    align-items: flex-start !important;
  }

  .renewal-expiry-months-input ::v-deep .v-select__selections {
    min-height: 50px !important;
    align-items: flex-start !important;
    align-content: flex-start !important;
    flex-wrap: wrap !important;
    row-gap: 2px !important;
    overflow: visible !important;
  }

  .renewal-expiry-months-input ::v-deep .v-select__selections input {
    flex: 0 0 1px !important;
    width: 1px !important;
    min-width: 1px !important;
    max-width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .renewal-expiry-months-input ::v-deep .v-chip {
    margin: 1px 4px 1px 0 !important;
    height: 18px !important;
    max-height: 18px !important;
  }

  .renewal-expiry-months-input ::v-deep .v-chip__content {
    padding: 0 1px !important;
    line-height: 18px !important;
    font-size: 11px !important;
  }

  .renewal-expiry-months-input ::v-deep .v-chip .v-chip__close {
    margin-left: 0 !important;
    font-size: 14px !important;
  }

  .renewal-expiry-months-field .v-input__append-inner {
    align-self: center !important;
    margin-top: 0 !important;
    margin-left: 2px !important;
    padding-left: 0 !important;
    gap: 0 !important;
    flex: 0 0 auto !important;
    opacity: 1 !important;
  }

  .renewal-expiry-months-field .v-input__append-inner .v-input__icon {
    min-width: 18px !important;
    width: 18px !important;
  }

  .renewal-expiry-months-field .v-input__icon--clear,
  .renewal-expiry-months-field .v-input__icon--clear button,
  .renewal-expiry-months-field .v-input__icon--clear .v-icon {
    opacity: 1 !important;
    visibility: visible !important;
    pointer-events: auto !important;
  }

  .renewal-expiry-months-field .v-input__icon--clear button {
    width: 18px !important;
    height: 18px !important;
  }

  .renewal-expiry-months-field .v-input__icon--clear .v-icon {
    font-size: 18px !important;
  }

  .renewal-loading-state {
    min-height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .renewal-loading-text {
    font-size: 18px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.78);
    text-align: center;
  }

  .renewal-premium-cell {
    width: 100%;
    display: inline-block;
    text-align: right;
    padding-right: 8px;
  }

  .renewal-table-footer {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 52px;
    padding: 0 12px;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-top: none;
    color: rgba(0, 0, 0, 0.6);
    font-size: 0.75rem;
  }

  .renewal-totals-band {
    background: #f5f5f5;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    color: rgba(0, 0, 0, 0.78);
  }

  .renewal-totals-band__subtitle {
    margin: 0 0 5px;
    padding: 0 12px;
    line-height: 1.1;
  }

  .renewal-totals-band__heading {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    column-gap: 2px;
  }

  .renewal-totals-band__inline-caption {
    line-height: 1.1;
  }

  .renewal-totals-band__title {
    padding: 4px 12px 2px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.68);
    line-height: 1.1;
  }

  .renewal-totals-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .renewal-totals-section {
    min-width: 0;
    border-left: 1px solid rgba(0, 0, 0, 0.12);
  }

  .renewal-totals-section:first-child {
    border-left: 0;
  }

  .renewal-totals-grid--headers .renewal-totals-section {
    padding: 2px 10px 1px;
  }

  .renewal-totals-grid--labels .renewal-totals-section,
  .renewal-totals-grid--values .renewal-totals-section {
    display: grid;
    grid-template-columns: minmax(0, 64px) minmax(0, 1fr);
    column-gap: 10px;
    align-items: center;
    padding: 2px 10px;
  }

  .renewal-totals-grid--labels .renewal-totals-section {
    padding-top: 1px;
    padding-bottom: 1px;
  }

  .renewal-totals-grid--values .renewal-totals-section {
    padding-top: 1px;
    padding-bottom: 5px;
  }

  .renewal-totals-section__title {
    font-size: 11px;
    font-weight: 700;
    line-height: 1.1;
  }

  .renewal-totals-metric-label {
    font-size: 10px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.56);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    line-height: 1.05;
  }

  .renewal-totals-metric-value {
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
    line-height: 1.1;
  }

  .renewal-totals-band--print {
    display: none;
  }

  .renewal-print-totals-row {
    display: none;
  }

  .renewal-print-totals-cell {
    padding: 0 !important;
    border: 0 !important;
    background: white !important;
  }

  .renewal-table-footer__rows,
  .renewal-table-footer__range {
    white-space: nowrap;
  }

  .renewal-table-footer__spacer {
    flex: 1 1 auto;
  }

  /* 7 May 2026 JCH: page header style */
  .renewalheader {
    color: black;
    font-size: larger !important;
  }

  .renewal-print-sort-note {
    display: none;
  }

  .renewal-print-page-note {
    display: none;
  }

  .renewal-print-view-filter-note {
    display: none;
  }

  @media (min-width: 960px) {
    .renewal-filter-card > .row {
      flex-wrap: nowrap;
    }

    .renewal-filter-card {
      height: 96px;
      min-height: 96px;
      max-height: 96px;
      padding-bottom: 0px !important;
    }

    .renewal-filter-col {
      padding-top: 14px !important;
      padding-bottom: 0 !important;
    }

    .renewal-filter-col:not(.renewal-filter-col--months) {
      flex: 0 0 22% !important;
      max-width: 22% !important;
    }

    .renewal-filter-col--months {
      display: flex;
      align-items: flex-start;
      flex: 0 0 34% !important;
      max-width: 34% !important;
      padding-top: 14px !important;
      padding-bottom: 0 !important;
    }

    .renewal-expiry-months-input ::v-deep .v-input__slot {
      min-height: 92px !important;
      padding-top: 0 !important;
      padding-bottom: 4px !important;
    }

    .renewal-expiry-months-input ::v-deep .v-select__selections {
      min-height: 86px !important;
    }

    .renewal-filter-col--months--two-rows .renewal-filter-group {
      transform: translateY(-4px);
    }

    .renewal-filter-col--months--three-rows .renewal-filter-group {
      transform: translateY(-8px);
    }

    .renewal-filter-col--months--four-rows .renewal-filter-group {
      transform: translateY(-12px);
    }
  }

  @media (max-width: 959px) {
    .renewal-toolbar {
      flex-wrap: wrap;
      padding-top: 4px;
      padding-bottom: 0px;
    }

    .renewal-toolbar__secondary {
      margin-left: auto;
    }

    .renewal-toolbar__reset {
      margin-left: auto;
    }

    .renewal-filter-col {
      padding-top: 18px !important;
      padding-bottom: 0 !important;
    }

    .renewal-filter-col--months {
      padding-top: 16px !important;
      padding-bottom: 0 !important;
    }

    .renewal-table-footer {
      padding-left: 8px;
      padding-right: 8px;
      gap: 4px;
    }

    .renewal-totals-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .renewal-totals-grid--values .renewal-totals-section {
      padding-bottom: 4px;
    }

  }

  .renewal-empty-results-spacer {
    height: 32px;
  }

  .renewal-empty-state {
    width: 100%;
    min-height: 74px;
    padding: 26px 16px 20px;
    color: rgba(0, 0, 0, 0.38);
    text-align: left;
  }

  .renewal-results-table--empty {
    margin-top: 0 !important;
  }

  /* 7 May 2026 JCH: table header style */
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
    background-color: #dcdcdc !important; /* 7 May 2026 JCH: opaque header prevents scrolled rows from bleeding through */
    border-bottom: 1px solid black;
    color: black;
    height: 40px;
    vertical-align: middle;
    white-space: nowrap;
    line-height: 1.1;
    background-clip: padding-box;
    z-index: 3;
  }

  .table-styl.theme--light.v-data-table > .v-data-table__wrapper {
    background: white;
  }

  .table-styl.theme--light.v-data-table > .v-data-footer {
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th > span,
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th .v-data-table-header__icon {
    display: inline-block;
    vertical-align: middle;
  }

  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th .v-data-table-header__sort-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    box-sizing: border-box;
    padding-top: 1px;
    line-height: 1;
  }

  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th.insured-col,
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr > td.insured-col {
    min-width: 200px;
    width: auto !important;
  }

  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th.actions-col,
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr > td.actions-col {
    min-width: 90px;
    width: 90px;
  }

  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th.producer-col,
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr > td.producer-col,
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th.solicitor-col,
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr > td.solicitor-col {
    min-width: 120px;
    width: 120px;
  }

  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th.premium-col,
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr > td.premium-col {
    text-align: right !important;
  }

  /* 7 May 2026 JCH: remove horizontal table row lines */
  .table-styl.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) >
    td:not(.v-data-table__mobile-row), .theme--light.v-data-table > .v-data-table__wrapper > table > tbody >
    tr:not(:last-child) > th:not(.v-data-table__mobile-row) {
    border-bottom: none;
  }

  /* 7 May 2026 JCH: add shading to alternate table rows */
  .striped-table tbody tr:nth-of-type(even) {
    background-color: rgba(0, 0, 0, .06);
  }

  /* 7 May 2026 JCH: position and style table for printing */
  @media print
  {
    body * { visibility: hidden }
    .renewal-results-section,
    .renewal-results-section * { visibility: visible }
    .renewal-results-section {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      padding: 0 12px !important;
      margin: 0 !important;
      background: white !important;
    }
    .renewal-results-section::after {
      content: '' !important;
      display: block !important;
      width: 100% !important;
      height: 8px !important;
      border-top: 1px solid rgba(0, 0, 0, 0.18) !important;
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0.14) 0%, rgba(0, 0, 0, 0.05) 45%, rgba(0, 0, 0, 0) 100%) !important;
    }
    .renewalheader {
      position: static !important;
      width: 100% !important;
      margin: 0 0 8px !important;
      padding: 0 !important;
      line-height: 1.1 !important;
      page-break-after: avoid !important;
    }
    .renewalheader h4 {
      font-size: 22px !important;
      line-height: 1.1 !important;
      margin-top: 0 !important;
      margin-bottom: 3px !important;
    }
    .renewalheader h5 {
      font-size: 15px !important;
      line-height: 1.1 !important;
      margin-top: 0 !important;
      margin-bottom: 2px !important;
    }
    .renewalheader h6 {
      font-size: 11px !important;
      line-height: 1.1 !important;
      margin-top: 0 !important;
      margin-bottom: 2px !important;
      white-space: normal !important;
    }
    .renewal-print-view-filter-note { display: block !important; font-size: 11px !important; font-weight: 600 !important }
    .renewal-print-sort-note { display: block !important; font-size: 11px !important; font-weight: 600 !important }
    .renewal-print-page-note { display: block !important; font-size: 11px !important; font-weight: 600 !important }
    .renewal-total-rows { display: none !important }
    .renewal-totals-band { display: none !important }
    .renewal-loading-state,
    .renewal-empty-results-spacer { display: none !important }
    .renewal-table-footer { display: none !important }
    .prtrenewal { position: static !important; top: auto !important; left: auto !important; width: 100% !important; max-width: 100% !important; height: auto !important; margin: 0 !important; box-shadow: none !important; border: 1px solid rgba(0, 0, 0, 0.22) !important; border-radius: 0 !important; box-sizing: border-box !important; -webkit-box-decoration-break: clone !important; box-decoration-break: clone !important; background: white !important }
    .prtrenewal .v-data-table__wrapper { overflow: visible !important; max-height: none !important; height: auto !important; box-shadow: none !important; border: 0 !important; border-radius: 0 !important; background: white !important }
    .prtrenewal .v-data-table__wrapper > table { border-collapse: collapse !important; border-spacing: 0 !important; border-radius: 0 !important; background: white !important; border-bottom: 0 !important }
    .prtrenewal .v-data-footer { display: none !important }
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th.actions-col,
    .prtrenewal .v-data-table__wrapper > table > tbody > tr > td.actions-col { display: none !important }
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th.producer-col,
    .prtrenewal .v-data-table__wrapper > table > tbody > tr > td.producer-col,
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th.solicitor-col,
    .prtrenewal .v-data-table__wrapper > table > tbody > tr > td.solicitor-col { display: none !important }
    .prtrenewal .v-data-table__wrapper > table { width: 100% !important; table-layout: auto !important; font-size: 10px !important }
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th.insured-col,
    .prtrenewal .v-data-table__wrapper > table > tbody > tr > td.insured-col { min-width: 0 !important; width: auto !important }
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th,
    .prtrenewal .v-data-table__wrapper > table > tbody > tr > td { padding: 0 4px !important; min-width: 0 !important; width: auto !important; max-width: none !important }
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th {
      height: 28px !important;
      vertical-align: middle !important;
      white-space: nowrap !important;
      line-height: 1.1 !important;
    }
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th > span {
      display: inline-block !important;
      vertical-align: middle !important;
    }
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th .v-data-table-header__icon,
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th .v-data-table-header__sort-badge {
      display: none !important;
    }
    .prtrenewal.renewal-results-table--empty {
      margin-top: 0 !important;
    }
    .prtrenewal .v-data-table__wrapper > table > thead > tr > th.premium-col,
    .prtrenewal .v-data-table__wrapper > table > tbody > tr > td.premium-col {
      text-align: right !important;
      padding-right: 2px !important;
    }
    .prtrenewal .renewal-premium-cell {
      padding-right: 0 !important;
      text-align: right !important;
    }
    .renewal-totals-band--print {
      display: block !important;
      margin-top: 4px !important;
      border: 1px solid black !important;
      background: white !important;
      box-shadow: none !important;
      page-break-inside: avoid !important;
    }
  }

  /* 7 May 2026 JCH: Page 2+: more top margin when the table continues. Page 1 keeps tight top for header block. */
  @page {
    margin: 0.35in 0.3in 0.45in 0.2in !important;
  }

  @page :first {
    margin: 0.1in 0.3in 0.45in 0.2in !important;
  }
</style>